import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { MultiSelectInput } from '@/components/ui/multi-select-input';
import { cn } from '@/lib/cn';
import type { BaseInstance, ChoiceField } from '@pigello/pigello-matrix';
import { nanoid } from 'nanoid';
import { useMemo } from 'react';
import type { Path } from 'react-hook-form';
import type { FieldProps } from '../types';
import { DescriptionTooltip } from './description-tooltip';

export function Array<Instance extends BaseInstance = BaseInstance>({
  formField,
  name,
  label,
  placeholder,
  description,
  horizontal,
  className,
  disabled,
  canHandleField,
  overrideRequired,
  choices,
}: FieldProps<Instance> & { choices?: ChoiceField<any>['choices'] }) {
  const renderKey = useMemo(() => nanoid(), []);

  const defaultValues =
    choices?.map(({ v, d }) => ({
      v,
      d,
    })) ?? [];

  if (horizontal) {
    return (
      <FormField
        key={renderKey}
        control={formField.control}
        name={name as Path<Instance>}
        rules={{
          required:
            (overrideRequired ?? formField.required)
              ? 'Detta fält är obligatoriskt'
              : undefined,
        }}
        render={({ field }) => (
          <FormItem
            className={cn('flex items-center justify-between', className)}
          >
            <div className='w-1/2 shrink-0'>
              <FormLabel>
                {label ?? formField.label}{' '}
                {(overrideRequired || formField.required) && <span>*</span>}
              </FormLabel>
              <FormDescription>
                {description ?? formField?.description}
              </FormDescription>
              <FormMessage />
            </div>
            <FormControl>
              <div className='w-full'>
                <MultiSelectInput
                  placeholder={`Välj ${placeholder ?? formField.label}`}
                  onSelected={(items) => {
                    field.onChange(items.map((i) => i.v));
                  }}
                  values={
                    field.value?.map((val: string) => ({
                      v: val,
                      d: defaultValues?.find((d: { v: string }) => d.v === val)
                        ?.d,
                    })) ?? []
                  }
                  defaultValues={defaultValues}
                  disabled={disabled ?? field.disabled ?? !canHandleField}
                />
              </div>
            </FormControl>
          </FormItem>
        )}
      />
    );
  }
  return (
    <FormField
      key={renderKey}
      control={formField.control}
      rules={{
        required: formField.required && 'Detta fält är obligatoriskt',
      }}
      name={name as Path<Instance>}
      render={({ field }) => (
        <FormItem className={className}>
          <div className='flex items-center'>
            <FormLabel>
              {label ?? formField.label} {formField.required && <span>*</span>}
            </FormLabel>
            {(description || formField.description) && (
              <DescriptionTooltip
                description={description ?? formField.description}
              />
            )}
          </div>
          <FormControl>
            <div className='w-full'>
              <MultiSelectInput
                placeholder={`Välj ${placeholder ?? formField.label}`}
                onSelected={(items) => {
                  field.onChange(items.map((i) => i.v));
                }}
                values={
                  field?.value?.map((val: string) => ({
                    v: val,
                    d: defaultValues?.find((d: { v: string }) => d.v === val)
                      ?.d,
                  })) ?? []
                }
                defaultValues={defaultValues}
                disabled={disabled ?? field.disabled ?? !canHandleField}
              />
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
